import React, { useState } from "react";
import {
  BookIcon,
  PencilIcon,
  GearIcon,
  QuestionIcon,
  ScreenFullIcon,
  TrashIcon,
  StarIcon,
  StarFillIcon,
} from "@primer/octicons-react";
import { Tooltip } from "reactstrap";
import Link from "next/link";

type Type =
  | "remove"
  | "edit"
  | "expand"
  | "settings"
  | "document"
  | "help"
  | "star"
  | "starFill";
type IconSize = "standard" | "large";

interface Props {
  type: Type;
  size?: IconSize;
  label?: string;
  onClick(): void;
  id?: string;
}

export default function IconButton(props: Props) {
  const id = props.id?.replaceAll(/[./]/g, "-") || "container";

  return (
    <span
      className="btn btn-link"
      style={{ cursor: "pointer" }}
      id={id}
      onClick={props.onClick}
    >
      <Icon
        type={props.type}
        label={props.label}
        targetId={id}
        size={props.size}
      />
    </span>
  );
}

export function IconLink(props: {
  type: Type;
  size?: IconSize;
  label?: string;
  href: string;
  id?: string;
}) {
  const id = props.id || "container";

  return (
    <Link href={props.href}>
      <a id={props.id}>
        <Icon
          type={props.type}
          label={props.label}
          targetId={id}
          size={props.size}
        />
      </a>
    </Link>
  );
}

function Icon(props: {
  type: Type;
  label?: string;
  targetId: string;
  size?: IconSize;
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggle = () => setTooltipOpen(!tooltipOpen);

  const label = props.label || defaultLabel(props.type);
  const IconComponent = icon(props.type);

  return (
    <>
      <IconComponent size={props.size === "large" ? 24 : 16} />
      <Tooltip
        key={label}
        placement="bottom"
        target={props.targetId}
        isOpen={tooltipOpen}
        toggle={toggle}
      >
        {label}
      </Tooltip>
    </>
  );
}

function icon(type: Type) {
  switch (type) {
    case "document":
      return BookIcon;
    case "edit":
      return PencilIcon;
    case "expand":
      return ScreenFullIcon;
    case "help":
      return QuestionIcon;
    case "remove":
      return TrashIcon;
    case "settings":
      return GearIcon;
    case "star":
      return StarIcon;
    case "starFill":
      return StarFillIcon;
    default:
      throw new Error(`Unknown icon type: ${type}`);
  }
}

function defaultLabel(type: Type) {
  switch (type) {
    case "document":
      return "Documentation";
    case "edit":
      return "Edit";
    case "expand":
      return "Expand";
    case "help":
      return "Help";
    case "remove":
      return "Remove";
    case "settings":
      return "Settings";
    case "star":
      return "";
    case "starFill":
      return "";
    default:
      throw new Error(`Unknown icon type: ${type}`);
  }
}
